import {
  readPreviousAnswer,
  isChoiceRows,
  type ChoiceRow,
  type SavedChoiceRow,
  type AnswerJson,
} from "../answerJson";
import {
  decodeVisualGroups,
  type VisualGrouping,
  type RawVisualGrouping,
} from "../visualGroups";
import { defaultThemeV2 } from "../../theme/defaultThemeV2";
import type { QuestionCore } from "../contracts";
import { encodeResponses } from "../responseEncoding";
import {
  encodeRowAnswer,
  restoreRowLabels,
  rowAnswer,
  rowLabelDraft,
  updateRowLabel,
  validateRowLabels,
  type RowLabelDraft,
  restoreChoice,
} from "../specify/core";
import type { GridJsonResponse, SpecifiedRowAnswer } from "../specify/types";

export type GridSelectRaw = RawVisualGrouping & {
  type: "grid_select";
  content: string;
  style: "button" | "radio";
  options: {
    value: number;
    text: string;
    disabled: boolean;
    pop_up_text?: string | null;
  }[];
  dont_know_option?: { value: string; text: string } | null;
  rows: {
    value: string;
    text: string;
    image?: string | null;
    display_text: boolean;
    pop_up_text?: string | null;
  }[];
  previous_response?: (number | string | SpecifiedRowAnswer<number | string>)[];
  image?: string | null;
  alt_text?: string | null;
  error_message?: string;
  previous_answer_json?: unknown;
};

export type GridSelectDefinition = VisualGrouping & {
  prompt: string;
  options: readonly {
    id: number;
    label: string;
    disabled: boolean;
    customPrompt?: string;
  }[];
  dontKnow?: { value: string; label: string };
  rows: readonly {
    id: string;
    label: string;
    image?: string;
    displayLabel: boolean;
    customPrompt?: string;
  }[];
  previousRowLabels?: Readonly<Record<string, string>>;
  previousResponses: readonly ChoiceRow[];
  image?: string;
  altText?: string;
  error?: string;
};

export type GridSelectSelection = {
  optionId: number | string;
  customText?: string;
};
export type GridSelectDraft = RowLabelDraft & {
  selectedByRow: Readonly<Record<string, GridSelectSelection>>;
};
export type GridSelectEvent =
  | { type: "select"; rowId: string; optionId: number | string }
  | { type: "clear"; rowId: string }
  | {
      type: "set-custom-answer";
      rowId: string;
      optionId: number;
      value: string;
    }
  | { type: "set-row-label"; rowId: string; value: string };
export type GridSelectResponse = {
  type: "grid_select";
  value: string;
  responses: string;
  responseJson?: GridJsonResponse;
  answerJson: AnswerJson;
};

const selectedValue = (
  definition: GridSelectDefinition,
  selection: GridSelectSelection | undefined,
) => {
  if (
    definition.dontKnow &&
    selection?.optionId === definition.dontKnow.value
  ) {
    return definition.dontKnow.value;
  }
  const option = definition.options.find(
    (item) => item.id === selection?.optionId,
  );
  if (!option || option.disabled) return undefined;
  if (option.customPrompt)
    return selection?.customText?.trim() ? selection.customText : undefined;
  return option.id;
};

export const gridSelectCore: QuestionCore<
  GridSelectRaw,
  GridSelectDefinition,
  GridSelectDraft,
  GridSelectEvent,
  GridSelectResponse
> = {
  decode: (raw) => {
    if (
      !Array.isArray(raw.options) ||
      raw.options.length === 0 ||
      !Array.isArray(raw.rows) ||
      raw.rows.length === 0
    ) {
      throw new Error("Grid select requires rows and options.");
    }
    const optionIds = raw.options.map((item) => item.value);
    const rowIds = raw.rows.map((item) => item.value);
    if (
      optionIds.some((id) => !Number.isInteger(id)) ||
      new Set(optionIds).size !== optionIds.length ||
      rowIds.some((id) => typeof id !== "string" || !id) ||
      new Set(rowIds).size !== rowIds.length
    ) {
      throw new Error("Grid select row and option IDs must be unique.");
    }
    if (
      raw.previous_answer_json === undefined &&
      raw.previous_response !== undefined &&
      (!Array.isArray(raw.previous_response) ||
        raw.previous_response.some(
          (value) =>
            typeof rowAnswer(value) !== "string" &&
            typeof rowAnswer(value) !== "number",
        ))
    ) {
      throw new Error(
        "Grid select previous response must be a list of values.",
      );
    }
    const previous = readPreviousAnswer<SavedChoiceRow[]>(
      raw.previous_answer_json,
      raw.previous_response ?? [],
      isChoiceRows,
    );
    return {
      ...decodeVisualGroups(raw),
      prompt: raw.content,
      options: raw.options.map((option) => ({
        id: option.value,
        label: option.text,
        disabled: option.disabled,
        ...(option.pop_up_text ? { customPrompt: option.pop_up_text } : {}),
      })),
      rows: raw.rows.map((row) => ({
        id: row.value,
        label: row.text,
        displayLabel: row.display_text,
        ...(row.pop_up_text != null ? { customPrompt: row.pop_up_text } : {}),
        ...(row.image ? { image: row.image } : {}),
      })),
      previousResponses: previous.map((value) => rowAnswer(value)),
      ...(raw.rows.some((row) => row.pop_up_text != null)
        ? {
            previousRowLabels: restoreRowLabels(
              raw.rows.map((row) => ({
                id: row.value,
                customPrompt: row.pop_up_text ?? undefined,
              })),
              [previous],
            ),
          }
        : {}),
      ...(raw.dont_know_option
        ? {
            dontKnow: {
              value: raw.dont_know_option.value,
              label: raw.dont_know_option.text,
            },
          }
        : {}),
      ...(raw.image ? { image: raw.image } : {}),
      ...(raw.alt_text ? { altText: raw.alt_text } : {}),
      ...(raw.error_message ? { error: raw.error_message } : {}),
    };
  },
  initialize: (definition) => {
    const selectedByRow = Object.create(null) as Record<
      string,
      GridSelectSelection
    >;
    definition.rows.forEach((row, index) => {
      const saved = definition.previousResponses[index];
      const raw = Array.isArray(saved) ? saved[0] : saved;
      if (raw === undefined || raw === "") return;
      if (definition.dontKnow && raw === definition.dontKnow.value) {
        selectedByRow[row.id] = { optionId: raw };
        return;
      }
      const restored = restoreChoice(definition.options, raw);
      if (restored) selectedByRow[row.id] = restored;
    });
    return { selectedByRow, ...rowLabelDraft(definition.previousRowLabels) };
  },
  update: (definition, draft, event) => {
    const row = definition.rows.find((item) => item.id === event.rowId);
    if (!row) return draft;
    if (event.type === "clear") {
      const selectedByRow = { ...draft.selectedByRow };
      delete selectedByRow[row.id];
      return { ...draft, selectedByRow };
    }
    if (event.type === "set-row-label") {
      return row.customPrompt === undefined
        ? draft
        : {
            ...draft,
            specifiedRows: updateRowLabel(
              draft.specifiedRows,
              row.id,
              event.value,
            ),
          };
    }
    if (
      event.type === "select" &&
      event.optionId === definition.dontKnow?.value
    ) {
      return {
        ...draft,
        selectedByRow: {
          ...draft.selectedByRow,
          [row.id]: { optionId: event.optionId },
        },
      };
    }
    const option = definition.options.find(
      (item) => item.id === event.optionId,
    );
    if (!option || option.disabled) return draft;
    if (event.type === "set-custom-answer") {
      if (option.customPrompt === undefined || !event.value.trim())
        return draft;
      return {
        ...draft,
        selectedByRow: {
          ...draft.selectedByRow,
          [row.id]: { optionId: option.id, customText: event.value },
        },
      };
    }
    if (option.customPrompt !== undefined) return draft;
    return {
      ...draft,
      selectedByRow: {
        ...draft.selectedByRow,
        [row.id]: { optionId: option.id },
      },
    };
  },
  validate: (definition, draft, copy = defaultThemeV2.copy) => {
    const labels = validateRowLabels(
      definition.rows,
      draft.specifiedRows ?? {},
    );
    if (!labels.valid) return labels;
    return definition.rows.every(
      (row) =>
        selectedValue(definition, draft.selectedByRow[row.id]) !== undefined,
    )
      ? { valid: true }
      : { valid: false, error: copy.validation.gridSelect };
  },
  encode: (definition, draft) => {
    const validation = gridSelectCore.validate(definition, draft);
    if (!validation.valid) throw new Error(validation.error);
    const values = definition.rows.map(
      (row) => selectedValue(definition, draft.selectedByRow[row.id])!,
    );
    return {
      type: "grid_select",
      value: values.join(","),
      responses: encodeResponses(values),
      answerJson: definition.rows.map((row) => {
        const selection = draft.selectedByRow[row.id];
        const answer =
          selection.customText !== undefined
            ? { custom: selection.customText }
            : typeof selection.optionId === "number"
              ? { option: selection.optionId }
              : selection.optionId;
        return encodeRowAnswer(row, draft.specifiedRows ?? {}, answer);
      }),
      ...(definition.rows.some((row) => row.customPrompt !== undefined)
        ? {
            responseJson: definition.rows.map((row, index) =>
              encodeRowAnswer(row, draft.specifiedRows!, String(values[index])),
            ),
          }
        : {}),
    };
  },
  projectTranscript: (definition, draft) => {
    const validation = gridSelectCore.validate(definition, draft);
    if (!validation.valid) throw new Error(validation.error);
    return {
      kind: "grid_select",
      question: definition.prompt,
      answer: definition.rows
        .map((row) => {
          const selection = draft.selectedByRow[row.id];
          if (
            definition.dontKnow &&
            selection.optionId === definition.dontKnow.value
          ) {
            return `${draft.specifiedRows?.[row.id] || row.label}: ${definition.dontKnow.label}`;
          }
          const option = definition.options.find(
            (item) => item.id === selection.optionId,
          )!;
          return `${draft.specifiedRows?.[row.id] || row.label}: ${option.customPrompt ? selection.customText : option.label}`;
        })
        .join("\n"),
    };
  },
};
